// Starts approximate sharing: radius, duration and, on /account, which circle.
import { useState, type FormEvent } from "react";
import {
  BUBBLE_DURATIONS,
  BUBBLE_RADII,
  DEFAULT_BUBBLE_DURATION,
  DEFAULT_BUBBLE_RADIUS,
  durationText,
  radiusText,
  type BubbleDuration,
} from "../sharing/bubbles";
import type { BubbleStart } from "../sharing/useSharingBubbles";
import { useUnits } from "../useUnits";

const ALL = "";

export function BubbleForm(props: {
  /** Offer a choice of circle (or every circle); without it the bubble covers `circleId`. */
  circles?: readonly { id: string; name: string }[];
  circleId?: string;
  /** The open-ended duration's label (default "Until I turn it off"). */
  offLabel?: string;
  /** The submit button's label (default "Share approximate area"). */
  submitLabel?: string | undefined;
  /** Whether the chosen scope (null = every circle) needs a guardian's approval: the button asks instead. */
  askFor?: (circleId: string | null) => boolean;
  busy: boolean;
  onStart: (start: BubbleStart) => void;
  onCancel?: () => void;
}) {
  const units = useUnits();
  const [scope, setScope] = useState(ALL);
  const [radiusM, setRadiusM] = useState<number>(DEFAULT_BUBBLE_RADIUS);
  const [hours, setHours] = useState<BubbleDuration>(DEFAULT_BUBBLE_DURATION);

  const chosen = props.circles ? (scope === ALL ? null : scope) : (props.circleId ?? null);
  const label = props.askFor?.(chosen)
    ? "Ask to share approximate area"
    : (props.submitLabel ?? "Share approximate area");

  function submit(event: FormEvent) {
    event.preventDefault();
    props.onStart({ circleId: chosen, radiusM, hours });
  }

  return (
    <form className="stack bubble-form" onSubmit={submit}>
      {props.circles && (
        <label>
          Share with
          <select value={scope} onChange={(event) => setScope(event.target.value)}>
            <option value={ALL}>Every circle</option>
            {props.circles.map((circle) => (
              <option key={circle.id} value={circle.id}>
                {circle.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Area
        <select value={radiusM} onChange={(event) => setRadiusM(Number(event.target.value))}>
          {BUBBLE_RADII.map((metres) => (
            <option key={metres} value={metres}>
              Within {radiusText(metres, units)}
            </option>
          ))}
        </select>
      </label>
      <label>
        For
        <select
          value={hours ?? ""}
          onChange={(event) =>
            setHours(event.target.value === "" ? null : (Number(event.target.value) as BubbleDuration))
          }
        >
          {BUBBLE_DURATIONS.map((option) => (
            <option key={option ?? "off"} value={option ?? ""}>
              {option === null && props.offLabel ? props.offLabel : durationText(option)}
            </option>
          ))}
        </select>
      </label>
      <div className="button-row">
        <button type="submit" disabled={props.busy}>
          {props.busy ? "Starting…" : label}
        </button>
        {props.onCancel && (
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
